import { zodResolver } from '@hookform/resolvers/zod'
import { CircleDollarSign, Eye, MessageSquareMore } from 'lucide-react'
import { useCallback, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import { Button } from '@/components/button'
import { PaymentJustificationContent } from '@/components/payment-justification/payment-justification-content'
import { PaymentJustificationModal } from '@/components/payment-justification/payment-justification-modal'
import { PaymentStatusBadge } from '@/components/payment-status-badge'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/tooltip'
import { InstallmentDataProps } from '@/models/finances-tab'
import {
  defaultPaymentJustificationValues,
  paymentJustificationStatusFormValidationSchema,
  PaymentJustificationStatusFormValues,
} from '@/schemas/backoffice/payment-justification-modal-schema'
import { FormatLocaleDateWithMonthAndYear } from '@/utils/format-locale-date'
import { generateUrl } from '@/utils/generate-url'

export function InstallmentData({
  justificationId,
  paymentProof,
  installmentNumber,
  monthlyAmount,
  referenceDate,
  paymentDate,
  paymentStatus,
  referenceDateWithoutFormat,
}: InstallmentDataProps) {
  const methodsPaymentJustification =
    useForm<PaymentJustificationStatusFormValues>({
      defaultValues: defaultPaymentJustificationValues,
      resolver: zodResolver(paymentJustificationStatusFormValidationSchema),
    })

  const [openPaymentJustificationModal, setOpenPaymentJustificationModal] =
    useState(false)

  const handleOpenPaymentJustificationModal = useCallback(() => {
    setOpenPaymentJustificationModal(true)
    const hasJustification = justificationId !== ''

    if (hasJustification) {
      return methodsPaymentJustification.reset({
        payment_justification_id: justificationId,
        reference_date: FormatLocaleDateWithMonthAndYear(
          referenceDateWithoutFormat,
        ),
        description_status: paymentStatus.Descricao,
      })
    }

    return methodsPaymentJustification.reset({
      reference_date: FormatLocaleDateWithMonthAndYear(
        referenceDateWithoutFormat,
      ),
      description_status: paymentStatus.Descricao,
      justification_file: [],
    })
  }, [
    justificationId,
    methodsPaymentJustification,
    paymentStatus.Descricao,
    referenceDateWithoutFormat,
  ])

  async function handleOpenCertificate(url: string) {
    await generateUrl({
      value: url,
      testId: 'finances-tab-installment-payment-proof-toast-error',
    })
  }

  return (
    <div className="flex justify-between gap-8 xl:gap-16">
      <div className="flex items-center gap-4 sm:gap-6 xl:gap-14">
        <CircleDollarSign className="text-monochromatic-300 h-9 w-9" />
        <div className="flex flex-col gap-2">
          <p className="text-monochromatic-500 text-base">Parcela</p>
          <p className="text-base font-medium text-neutral-700">
            {installmentNumber}ª
          </p>
        </div>
        <div className="flex flex-col gap-2 xl:pr-32">
          <p className="text-monochromatic-500 text-base">Valor mensal</p>
          <div className="flex items-center gap-2">
            <p className="text-bios-600 text-base font-semibold">
              {monthlyAmount}
            </p>

            <div className="flex w-4 justify-center">
              {paymentProof && (
                <button
                  type="button"
                  onClick={() => handleOpenCertificate(paymentProof)}
                  className="hover:opacity-70"
                  data-testid={`backoffice-finances-tab-payment-proof-view-button-${installmentNumber}`}
                >
                  <Eye className="text-monochromatic-300" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <p className="text-monochromatic-500 text-base">Data referência</p>
        <p className="text-base font-medium text-neutral-700">
          {referenceDate}
        </p>
      </div>

      <div className="flex flex-col gap-2">
        <p className="text-monochromatic-500 text-base">Data repasse</p>
        <p className="text-base font-medium text-neutral-700">{paymentDate}</p>
      </div>

      <div className="flex flex-col gap-2">
        <p className="text-monochromatic-500 text-center text-base">
          Status pagamento
        </p>

        {paymentStatus.Descricao && (
          <PaymentStatusBadge
            className="mx-auto text-center"
            variant={paymentStatus.Id_status}
            description={paymentStatus.Descricao}
          />
        )}
      </div>
      <div className="flex flex-col gap-2">
        <p className="text-monochromatic-500 text-center text-base">
          Justificativa
        </p>

        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                disabled={!justificationId}
                onClick={() => {
                  handleOpenPaymentJustificationModal()
                }}
                className="mx-auto"
                size="icon"
                variant="ghost"
                data-testid="installments-payment-justification-button"
              >
                <MessageSquareMore className="size-5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Justificativa do pagamento</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      <FormProvider {...methodsPaymentJustification}>
        <PaymentJustificationModal
          openModal={openPaymentJustificationModal}
          openChangeModal={setOpenPaymentJustificationModal}
          descriptionStatus={
            methodsPaymentJustification.getValues('description_status') || ''
          }
          referenceDate={
            methodsPaymentJustification.getValues('reference_date') || ''
          }
        >
          <PaymentJustificationContent />
        </PaymentJustificationModal>
      </FormProvider>
    </div>
  )
}
